/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { createContext, type ForwardedRef, forwardRef, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { type ContextValue, DEFAULT_SLOT, Provider, useContextProps } from 'react-aria-components';
import { useObjectRef } from '@react-aria/utils';
import { $ } from '../../tokens.stylex';
import { AvatarContext } from '../avatar/avatar.component';
import { BadgeButtonContext } from '../badge-button/badge-button.component';
import { BadgeContext } from '../badge/badge.component';
import { BreadcrumbsContext } from '../breadcrumbs/breadcrumbs.component';
import { ButtonContext } from '../button/button.component';
import { CloseSidebarButtonContext } from '../close-sidebar-button/close-sidebar-button.component';
import { HeadingContext } from '../heading/heading.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { InlineTextAreaContext } from '../inline-text-area/inline-text-area.component';
import { SegmentedButtonGroupContext } from '../segmented-button-group/segmented-button-group.component';
import { TextButtonContext } from '../text-button/text-button.component';
import { TextFieldStyleContext } from '../text-field/text-field.component';
import { TextContext } from '../text/text.component';
import { ViewContext } from '../view/view.component';
import { breakpoints } from '../../constants.stylex';

interface DetailsToolbarProps {
  children?: ReactNode;
  style?: stylex.StyleXStyles;
}

const DetailsToolbarContext = createContext<ContextValue<DetailsToolbarProps, HTMLDivElement>>({});

const SCROLL_THRESHOLD = 50;

function getScrollParent(element: HTMLElement | null): HTMLElement | Window {
  if (!element || element === document.documentElement) return window;
  const { overflow, overflowY } = getComputedStyle(element);
  if (/(auto|scroll)/.test(overflow + overflowY)) {
    return element;
  }
  return getScrollParent(element.parentElement);
}

const DetailsToolbar = forwardRef((props: DetailsToolbarProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, DetailsToolbarContext);

  const { style, children, ...coreProps } = props;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const objRef = useObjectRef(ref);
  const scrollUpStartRef = useRef<number | null>(null);
  const scrollDownStartRef = useRef<number | null>(null);
  const prevScrollYRef = useRef(0);
  const cooldownRef = useRef(false);

  useLayoutEffect(() => {
    const scrollParent = getScrollParent(objRef.current?.parentElement ?? null);

    const getScrollTop = () =>
      scrollParent instanceof HTMLElement ? scrollParent.scrollTop : window.scrollY;

    const handleScroll = () => {
      const y = getScrollTop();
      const direction = y > prevScrollYRef.current ? 'down' : y < prevScrollYRef.current ? 'up' : 'none';
      prevScrollYRef.current = y;

      setIsScrolled(y > 0);

      if (cooldownRef.current) {
        prevScrollYRef.current = y;
        scrollUpStartRef.current = null;
        scrollDownStartRef.current = null;
        return;
      }

      if (y <= 0) {
        setIsCollapsed(false);
        scrollUpStartRef.current = null;
        scrollDownStartRef.current = null;
        return;
      }

      if (direction === 'down') {
        scrollUpStartRef.current = null;

        if (scrollDownStartRef.current === null) {
          scrollDownStartRef.current = y;
        }

        if (y - scrollDownStartRef.current >= SCROLL_THRESHOLD) {
          setIsCollapsed(true);
          scrollDownStartRef.current = null;
          cooldownRef.current = true;
          setTimeout(() => { cooldownRef.current = false; }, 200);
        }
      } else if (direction === 'up') {
        scrollDownStartRef.current = null;

        if (scrollUpStartRef.current === null) {
          scrollUpStartRef.current = y;
        }

        if (scrollUpStartRef.current - y >= SCROLL_THRESHOLD) {
          setIsCollapsed(false);
          scrollUpStartRef.current = null;
          cooldownRef.current = true;
          setTimeout(() => { cooldownRef.current = false; }, 200);
        }
      }
    };

    handleScroll();
    scrollParent.addEventListener('scroll', handleScroll, { passive: true });

    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={objRef} {...stylex.props(styles.base, styles.sticky, isScrolled && styles.withBorder, style)} {...coreProps}>
      <Provider values={[
        [TextFieldStyleContext, {
          style: textFieldStyles.base,
        }],
        [ViewContext, {
          slots: {
            [DEFAULT_SLOT]: {
              style: viewStyles.group,
            },
            [DETAILS_TOOLBAR_SLOTS.ROW_VIEW]: {
              style: viewStyles.row,
            },
            [DETAILS_TOOLBAR_SLOTS.COLLAPSIBLE_ROW_VIEW]: {
              style: [viewStyles.collapsibleRow, isCollapsed && viewStyles.collapsedRow],
            },
            [DETAILS_TOOLBAR_SLOTS.PAGE_HEADER_VIEW]: {
              style: [viewStyles.group, viewStyles.pageHeaderView]
            },
            [DETAILS_TOOLBAR_SLOTS.PAGE_SUBTITLE_VIEW]: {
              style: [viewStyles.group, viewStyles.pageSubtitleView],
            }
          }
        }],
        [HeadingContext, {
          level: 1,
          size: 20,
          style: [inlineTextAreaStyles.base, headingStyles.base],
        }],
        [TextButtonContext, {
          slots: {
            [DEFAULT_SLOT]: {},
            [DETAILS_TOOLBAR_SLOTS.COPY_ID_TEXT_BUTTON]: {
              variant: 'tertiary',
              style: {
                base: copyIdTextButtonStyles.base,
                text: copyIdTextButtonStyles.text,
              },
            }
          }
        }],
        [TextContext, {
          slots: {
            [DEFAULT_SLOT]: {
              size: 14,
              style: textStyles.base
            },
            [DETAILS_TOOLBAR_SLOTS.PAGE_TYPE_TEXT]: {
              size: 14,
              color: 'placeholder',
              style: textStyles.pageType
            }
          }
        }],
        [IconButtonContext, {
          variant: 'ghost',
          size: 40,
          isRounded: true,
        }],
        [CloseSidebarButtonContext, {
          isRounded: false,
          style: closeSidebarIconButtonStyles.base,
        }],
        [AvatarContext, {
          size: 32,
          style: avatarStyles.base,
        }],
        [SegmentedButtonGroupContext, {
          size: 32,
          disallowEmptySelection: true,
        }],
        [ButtonContext, {
          variant: 'ghost',
          size: 40,
        }],
      ]}>
        <Provider values={[
          [InlineTextAreaContext, {
            size: 20,
            variant: 'heading',
            maxLines: isCollapsed ? 1 : 3,
            style: inlineTextAreaStyles.base,
          }],
          [BreadcrumbsContext, {
            style: breadcrumbsStyles.base,
          }],
          [BadgeContext, {
            size: 32,
            isPastel: true,
            isRounded: true,
          }],
          [BadgeButtonContext, {
            size: 32,
            isPastel: true,
            isRounded: true,
          }]
        ]}>
          {children}
        </Provider>
      </Provider>
    </div>
  );
});

const styles = stylex.create({
  base: {
    backgroundColor: $.surfaceBGPrimary,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 12,
    paddingLeft: 20,
    paddingRight: 12,
    paddingTop: 8,
    width: '100%',
  },
  sticky: {
    position: 'sticky',
    zIndex: 10,
    top: 0,
  },
  withBorder: {
    borderBottomColor: $.border06,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
  },
});

const viewStyles = stylex.create({
  row: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: 8,
    minHeight: 28,
    width: '100%',
  },
  collapsibleRow: {
    overflow: 'hidden',
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    opacity: 1,
    rowGap: 8,
    transitionDuration: '200ms',
    transitionProperty: 'max-height, opacity',
    transitionTimingFunction: 'ease',
    maxHeight: 60,
    width: '100%',
  },
  collapsedRow: {
    opacity: 0,
    maxHeight: 0,
  },
  group: {
    gap: 8,
    alignItems: 'center',
    display: 'flex',
    minWidth: 0,
  },
  pageHeaderView: {
    flexWrap: 'wrap',
    textWrap: 'nowrap',
    alignItems: 'baseline',
    width: '100%',
  },
  pageSubtitleView: {
    flexWrap: 'wrap',
  }
});

const avatarStyles = stylex.create({
  base: {
    margin: 4
  }
});

const textFieldStyles = stylex.create({
  base: {
    maxWidth: '100%',
    minWidth: 0,
  },
});

const headingStyles = stylex.create({
  base: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': {
      default: '3',
      [breakpoints.small]: '2',
    },
    '-webkit-box-orient': 'vertical',
  }
})

const inlineTextAreaStyles = stylex.create({
  base: {
    overflow: 'hidden',
    gridTemplateColumns: 'minmax(0, auto)',
    maxWidth: '100%',
  },
});

const textStyles = stylex.create({
  base: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: 'auto',
  },
  pageType: {
    flexShrink: 0,
    marginTop: 'auto',
  }
});

const copyIdTextButtonStyles = stylex.create({
  base: {
    overflow: 'hidden',
    flexShrink: 0,
    marginTop: 'auto',
    maxWidth: '100%'
  },
  text: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: 'auto',
  },
});

const closeSidebarIconButtonStyles = stylex.create({
  base: {
    marginLeft: -8,
  }
});

const breadcrumbsStyles = stylex.create({
  base: {
    minHeight: 40,
    marginRight: -8,
  },
});

const DETAILS_TOOLBAR_SLOTS = {
  COLLAPSIBLE_ROW_VIEW: 'collapsible-row-view',
  ROW_VIEW: 'row-view',
  PAGE_HEADER_VIEW: 'page-header-view',
  PAGE_TYPE_TEXT: 'page-type-text',
  COPY_ID_TEXT_BUTTON: 'copy-id-text-button',
  PAGE_SUBTITLE_VIEW: 'page-subtitle-view'
};

export { DETAILS_TOOLBAR_SLOTS, DetailsToolbar, DetailsToolbarContext, type DetailsToolbarProps };
